import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, TextField, IconButton, Tooltip, Link, Alert, CircularProgress, Stack } from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

export type ProductDocumentType = 'Msds' | 'Label';

const DOCUMENT_TYPES: { type: ProductDocumentType; label: string }[] = [
  { type: 'Msds', label: 'MSDS' },
  { type: 'Label', label: 'Label' },
];

interface ProductDocument {
  id: string;
  documentType: ProductDocumentType;
  fileName: string;
  sizeBytes: number;
  issueDate?: string;
  uploadedByName?: string;
  dateCreated: string;
  archivedDate?: string;
}

// A PDF picked while creating a product: there's no ProductId to upload it
// against yet, so ProductManagement uploads it right after the product is
// created (the same staged pattern as its Active Ingredient/Category rows).
export interface StagedProductDocument {
  documentType: ProductDocumentType;
  file: File;
  issueDate: string;
}

export const uploadProductDocument = async (
  productId: string,
  documentType: ProductDocumentType,
  file: File,
  issueDate: string,
  headers: Record<string, string>
) => {
  const form = new FormData();
  form.append('file', file);
  form.append('documentType', documentType);
  if (issueDate) form.append('issueDate', issueDate);
  const response = await fetch(`/api/products/${productId}/documents`, { method: 'POST', headers, body: form });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to upload document');
  }
};

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const formatDate = (value: string) => new Date(value).toLocaleDateString();

// Opens a PDF in a new tab. The tab is opened before the fetch, while the
// click still counts as a user action, so popup blockers allow it.
const openPdf = async (load: () => Promise<Blob>) => {
  const tab = window.open('', '_blank');
  let blob: Blob;
  try {
    blob = await load();
  } catch (err) {
    tab?.close();
    throw err;
  }
  const url = URL.createObjectURL(blob);
  if (tab) {
    tab.location.href = url;
  } else {
    window.location.assign(url);
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

// Opens one of a product's stored PDFs. The file endpoint needs the auth
// header, so it can't be a plain link.
export const openProductDocument = (productId: string, documentId: string, headers: Record<string, string>) =>
  openPdf(async () => {
    const response = await fetch(`/api/products/${productId}/documents/${documentId}/file`, { headers });
    if (!response.ok) throw new Error('Failed to load document');
    return response.blob();
  });

interface Props {
  // Null while the product is being created — picks are staged instead.
  productId: string | null;
  staged: StagedProductDocument[];
  onStagedChange: (staged: StagedProductDocument[]) => void;
  // After an upload/archive, so the product list's Docs column can refresh.
  onChanged?: () => void;
}

// A product's MSDS and label PDFs (see ProductDocument.cs): the current one
// of each type, its older versions, and an upload that supersedes it.
const ProductDocumentsPanel: React.FC<Props> = ({ productId, staged, onStagedChange, onChanged }) => {
  const auth = useAuth();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);

  const [documents, setDocuments] = useState<ProductDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyType, setBusyType] = useState<ProductDocumentType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [issueDates, setIssueDates] = useState<Record<ProductDocumentType, string>>({ Msds: '', Label: '' });
  const [historyOpen, setHistoryOpen] = useState<Record<ProductDocumentType, boolean>>({ Msds: false, Label: false });
  const fileInputs = useRef<Record<ProductDocumentType, HTMLInputElement | null>>({ Msds: null, Label: null });

  const fetchDocuments = useCallback(async () => {
    if (!productId) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`/api/products/${productId}/documents?includeArchived=true`, { headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to load documents');
      setDocuments(await response.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [productId, authHeaders]);

  useEffect(() => {
    setError(null);
    setIssueDates({ Msds: '', Label: '' });
    setHistoryOpen({ Msds: false, Label: false });
    fetchDocuments();
  }, [fetchDocuments]);

  const handleFilePicked = async (type: ProductDocumentType, file: File | undefined) => {
    if (fileInputs.current[type]) fileInputs.current[type]!.value = '';
    if (!file) return;
    setError(null);
    if (!productId) {
      onStagedChange([...staged.filter((s) => s.documentType !== type), { documentType: type, file, issueDate: issueDates[type] }]);
      return;
    }
    setBusyType(type);
    try {
      await uploadProductDocument(productId, type, file, issueDates[type], authHeaders());
      setIssueDates((prev) => ({ ...prev, [type]: '' }));
      await fetchDocuments();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setBusyType(null);
    }
  };

  const handleArchive = async (doc: ProductDocument) => {
    if (!productId || !window.confirm(`Archive ${doc.fileName}? It stays in this product's history.`)) return;
    setBusyType(doc.documentType);
    try {
      const response = await fetch(`/api/products/${productId}/documents/${doc.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to archive document');
      await fetchDocuments();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setBusyType(null);
    }
  };

  const viewDocument = (doc: ProductDocument) =>
    openProductDocument(productId!, doc.id, authHeaders())
      .catch((err) => setError(err instanceof Error ? err.message : 'An error occurred'));

  const renderDocumentLine = (doc: ProductDocument) => (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      <PdfIcon fontSize="small" color="error" />
      <Link component="button" variant="body2" onClick={() => viewDocument(doc)} sx={{ textAlign: 'left', wordBreak: 'break-all' }}>
        {doc.fileName}
      </Link>
      <Typography variant="caption" color="text.secondary">
        {[
          doc.issueDate ? `issued ${formatDate(doc.issueDate)}` : null,
          formatSize(doc.sizeBytes),
          `uploaded ${formatDate(doc.dateCreated)}${doc.uploadedByName ? ` by ${doc.uploadedByName}` : ''}`,
          doc.archivedDate ? `archived ${formatDate(doc.archivedDate)}` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </Typography>
    </Box>
  );

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>Documents</Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {loading ? (
        <CircularProgress size={20} />
      ) : (
        <Stack spacing={1.5}>
          {DOCUMENT_TYPES.map(({ type, label }) => {
            const current = documents.find((d) => d.documentType === type && !d.archivedDate);
            const history = documents.filter((d) => d.documentType === type && d.archivedDate);
            const stagedDoc = staged.find((s) => s.documentType === type);
            return (
              <Box key={type} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>{label}</Typography>
                    {current ? (
                      renderDocumentLine(current)
                    ) : stagedDoc ? (
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <PdfIcon fontSize="small" color="error" />
                        <Link component="button" variant="body2" onClick={() => openPdf(async () => stagedDoc.file)}>
                          {stagedDoc.file.name}
                        </Link>
                        <Typography variant="caption" color="text.secondary">
                          {formatSize(stagedDoc.file.size)} · uploads when the product is created
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No {label} yet.</Typography>
                    )}
                  </Box>
                  {current && (
                    <Tooltip title={`Archive this ${label}`}>
                      <span>
                        <IconButton size="small" color="error" disabled={busyType === type} onClick={() => handleArchive(current)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {stagedDoc && (
                    <Tooltip title="Remove">
                      <IconButton size="small" color="error" onClick={() => onStagedChange(staged.filter((s) => s !== stagedDoc))}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ mt: 1.5 }}>
                  <TextField
                    size="small"
                    type="date"
                    label="Issue date (optional)"
                    InputLabelProps={{ shrink: true }}
                    value={issueDates[type]}
                    onChange={(e) => {
                      const value = e.target.value;
                      setIssueDates((prev) => ({ ...prev, [type]: value }));
                      if (stagedDoc) onStagedChange(staged.map((s) => (s === stagedDoc ? { ...s, issueDate: value } : s)));
                    }}
                    sx={{ width: 190 }}
                  />
                  <input
                    ref={(el) => { fileInputs.current[type] = el; }}
                    type="file"
                    accept="application/pdf,.pdf"
                    hidden
                    onChange={(e) => handleFilePicked(type, e.target.files?.[0])}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={busyType === type ? <CircularProgress size={14} /> : <UploadFileIcon />}
                    disabled={busyType === type}
                    onClick={() => fileInputs.current[type]?.click()}
                  >
                    {current || stagedDoc ? `Replace ${label}` : `Upload ${label}`}
                  </Button>
                  {history.length > 0 && (
                    <Button
                      size="small"
                      startIcon={<HistoryIcon />}
                      onClick={() => setHistoryOpen((prev) => ({ ...prev, [type]: !prev[type] }))}
                    >
                      {historyOpen[type] ? 'Hide' : 'Show'} older ({history.length})
                    </Button>
                  )}
                </Box>
                {current && (
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                    Replacing keeps this one under older versions.
                  </Typography>
                )}

                {historyOpen[type] && (
                  <Stack spacing={0.75} sx={{ mt: 1.5, pl: 1, borderLeft: 2, borderColor: 'divider' }}>
                    {history.map((doc) => (
                      <Box key={doc.id}>{renderDocumentLine(doc)}</Box>
                    ))}
                  </Stack>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default ProductDocumentsPanel;
